import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Typography,
  Box,
  Link,
  Paper,
  Divider,
  useTheme
} from '@mui/material';


interface EnhancedMarkdownRendererProps {
  content: string;
  variant?: 'dialog' | 'full';
  skipFirstH1?: string;
}

const EnhancedMarkdownRenderer: React.FC<EnhancedMarkdownRendererProps> = ({ 
  content, 
  variant = 'full',
  skipFirstH1
}) => {
  const theme = useTheme();
  let isFirstH1 = true;





  return (
    <Box sx={{ 
      maxHeight: '100%',
      overflow: 'auto',
      pr: 1,
      '& h1, & h2, & h3, & h4, & h5, & h6': { 
        color: 'text.primary',
        fontWeight: 600,
        mt: variant === 'dialog' ? 2 : 3,
        mb: 2
      },
      '& h1': {
        fontSize: variant === 'dialog' ? '1.5rem' : '2rem',
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        pb: 1
      },
      '& h2': {
        fontSize: variant === 'dialog' ? '1.25rem' : '1.5rem',
        color: 'primary.main'
      },
      '& p': {
        color: 'text.primary',
        lineHeight: 0.9,
        mb: 2.5,
        textAlign: 'left',
        wordBreak: 'break-word'
      },
      '& ul, & ol': {
        color: 'text.primary',
        pl: 2,
        mb: 2
      },
      '& ul': {
        listStyleType: 'disc',
        '& ul': {
          paddingLeft: 3,
          listStyleType: 'circle'
        },
        '& ul ul': {
          paddingLeft: 4,
          listStyleType: 'square'
        }
      },
      '& li': {
        mb: 0.25,
        lineHeight: 0.9
      },
      '& code': {
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
        color: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.800',
        padding: '2px 6px',
        borderRadius: 1,
        fontSize: '0.875rem',
        fontFamily: 'monospace'
      },
      '& pre': {
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        p: 2,
        borderRadius: 1,
        overflow: 'auto',
        mb: 2
      },
      '& blockquote': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        pl: 2,
        py: 1,
        mb: 2,
        fontStyle: 'italic',
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        borderRadius: '0 4px 4px 0'
      },
      '& a': {
        color: 'primary.main',
        textDecoration: 'underline',
        '&:hover': {
          textDecoration: 'none'
        }
      }
    }}>
      <ReactMarkdown
        components={{
          p: ({ children }) => {
            // Regular paragraph
            return (
              <Typography 
                variant="body1" 
                component="p" 
                sx={{
                  color: 'text.primary',
                  lineHeight: 0.9,
                  mb: 2.5,
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {children}
              </Typography>
            );
          },
          h1: ({ children }) => {
            const shouldSkip = isFirstH1 && skipFirstH1 && 
              String(children).toLowerCase().trim() === skipFirstH1.toLowerCase().trim();
            isFirstH1 = false;
            
            if (shouldSkip) {
              return null;
            }
            
            return (
              <Typography 
                variant={variant === 'dialog' ? 'h5' : 'h4'} 
                component="h1"
                sx={{ 
                  fontWeight: 600, 
                  mb: 2, 
                  mt: 3,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  pb: 1,
                  color: 'text.primary'
                }}
              >
                {children}
              </Typography>
            );
          },
          h2: ({ children }) => (
            <>
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Typography
                variant={variant === 'dialog' ? 'h6' : 'h5'}
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: 'primary.main'
                }}
              >
                {children}
              </Typography>
            </>
          ),
          h3: ({ children }) => (
            <Typography 
              variant="subtitle1" 
              component="h3"
              sx={{ 
                fontWeight: 600, 
                mb: 1, 
                mt: 2,
                color: 'text.primary'
              }}
            >
              {children}
            </Typography>
          ),
          a: ({ children, href }) => (
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none'
                },
                wordBreak: 'break-word'
              }}
            >
              {children}
            </Link>
          ),
          strong: ({ children }) => (
            <Typography component="strong" sx={{ fontWeight: 700 }}>
              {children}
            </Typography>
          ),
          em: ({ children }) => (
            <Typography component="em" sx={{ fontStyle: 'italic' }}>
              {children}
            </Typography>
          ),
          blockquote: ({ children }) => (
            <Paper
              elevation={0}
              sx={{
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                pl: 2,
                py: 1,
                mb: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                borderRadius: '0 4px 4px 0'
              }}
            >
              <Typography component="div" sx={{ fontStyle: 'italic' }}>
                {children}
              </Typography>
            </Paper>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default EnhancedMarkdownRenderer;